import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Star, MapPin, Camera, Trash2 } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { CardGridSkeleton } from '../../components/shared/ClientPageSkeleton';
import { useFavoriteVendors, useRemoveFavorite } from '../../services/favoriteVendorService';
import { useToastContext } from '../../contexts/ToastContext';

const ClientFavorites = () => {
  const toast = useToastContext();
  
  // Fetch favorite vendors from API
  const { data: favoritesResponse, isLoading, error } = useFavoriteVendors();
  
  // Remove favorite mutation
  const removeFavoriteMutation = useRemoveFavorite({
    onSuccess: () => {
      toast.success('Removed from Favorites', 'Vendor has been removed from your favorites.');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove from favorites';
      toast.error('Error', errorMessage);
    }
  });

  // Extract favorites from paginated response
  const favorites = favoritesResponse?.data?.data || favoritesResponse?.data || [];

  const handleRemoveFavorite = (favoriteId, vendorId) => {
    removeFavoriteMutation.mutate({ favoriteId, vendorId });
  };

  // Helper function to get location string
  const getLocation = (vendor) => {
    const parts = [];
    if (vendor?.city) parts.push(vendor.city);
    if (vendor?.state) parts.push(vendor.state);
    if (vendor?.country && !parts.includes(vendor.country)) parts.push(vendor.country);
    return parts.length > 0 ? parts.join(', ') : 'Location not available';
  };

  // Helper function to get first service name
  const getFirstServiceName = (vendor) => {
    const services = vendor?.vendorServices || [];
    if (services.length > 0) {
      const service = services[0];
      return service?.serviceCategory?.category_name || service?.name || 'Service';
    }
    return 'Service';
  };

  // Helper function to get starting price
  const getStartingPrice = (vendor) => {
    const services = vendor?.vendorServices || [];
    if (services.length > 0) {
      // Find the minimum base_price
      const prices = services
        .map(s => s.base_price)
        .filter(p => p !== null && p !== undefined);
      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }
    return null;
  };

  // Helper function to get vendor image
  const getVendorImage = (vendor) => {
    return vendor?.image_url || vendor?.user?.profile_image_url || null;
  };

  if (isLoading) {
    return (
      <ClientPageLayout title="My Favorites">
        <div className="bg-white rounded-lg border border-black p-4 sm:p-6">
          <div className="h-6 w-48 bg-gray-200 rounded mb-6 animate-pulse"></div>
          <CardGridSkeleton count={4} />
        </div>
      </ClientPageLayout>
    );
  }

  if (error) {
    return (
      <ClientPageLayout title="My Favorites">
        <div className="bg-white rounded-lg border border-black p-4 sm:p-6">
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">Failed to load favorites. Please try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-block bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-900 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout title="My Favorites">
      <div className="bg-white rounded-lg border border-black p-4 sm:p-6">
        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-bold text-black mb-2">No Favorites Yet</h3>
            <p className="text-gray-600 mb-6">Start browsing vendors and add your favorite services!</p>
            <Link
              to="/browse"
              className="inline-block bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-900 transition-colors"
            >
              Browse Vendors
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-black">
                {favorites.length} Favorite{favorites.length !== 1 ? 's' : ''}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {favorites.map((favorite) => {
                const vendor = favorite.vendor || {};
                const vendorId = vendor.id || favorite.vendor_id;
                const favoriteId = favorite.id;
                const vendorImage = getVendorImage(vendor);
                const location = getLocation(vendor);
                const serviceName = getFirstServiceName(vendor);
                const startingPrice = getStartingPrice(vendor);
                const rating = vendor.average_rating || 0;
                const reviews = vendor.total_reviews || 0;

                return (
                  <div
                    key={favoriteId}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1">
                        {vendorImage ? (
                          <img
                            src={vendorImage}
                            alt={vendor.business_name || 'Vendor'}
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0 ${vendorImage ? 'hidden' : ''}`}
                        >
                          <Camera className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-base text-black truncate">
                            {vendor.business_name || 'Vendor'}
                          </h3>
                          <p className="text-sm text-gray-600 truncate">
                            {serviceName}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFavorite(favoriteId, vendorId)}
                        disabled={removeFavoriteMutation.isPending}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove from favorites"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="space-y-2 mb-4">
                      {rating > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Star className="w-4 h-4 text-black fill-current mr-1" />
                          <span className="font-semibold text-black mr-1">
                            {Number(rating).toFixed(1)}
                          </span>
                          <span>({reviews} {reviews === 1 ? 'review' : 'reviews'})</span>
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-black mr-1" />
                        <span>{location}</span>
                      </div>
                      {startingPrice !== null && (
                        <div className="text-base font-bold text-black">
                          Starting from ${Number(startingPrice).toLocaleString()}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Link
                        to={`/vendor/${vendorId}`}
                        className="flex-1 bg-black text-white text-center py-2 rounded-lg hover:bg-gray-900 transition-colors text-sm font-medium"
                      >
                        View Details
                      </Link>
                      <Link
                        to={`/vendor/${vendorId}/services`}
                        className="flex-1 border border-black text-black text-center py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                      >
                        Book Now
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ClientPageLayout>
  );
};

export default ClientFavorites;

